// src/components/pages/admin-dash/JobRunsAdmin.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Button from "../../ui/Button";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";

/**
 * Admin view over job_run (JobRunAdminResource, /admin/job-run) — most
 * recent execution of each @Scheduled job. Previously only visible by
 * reading container logs (1-ADMIN_ROADMAP.md item 2.7).
 */
const relTime = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const durationLabel = (startedAt, finishedAt) => {
  if (!startedAt || !finishedAt) return "—";
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

function StatusBadge({ run }) {
  if (run.success == null) return <Badge variant="gold">Running…</Badge>;
  return run.success ? <Badge variant="teal">OK</Badge> : <Badge variant="red">Failed</Badge>;
}

export default function JobRunsAdmin({ onShowToast }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRuns = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/job-run/latest");
      setRuns(Array.isArray(res.data) ? res.data : []);
    } catch {
      onShowToast?.("Couldn't load job run history.", "error");
      setRuns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRuns(); }, []); // eslint-disable-line

  const columns = [
    { key: "jobName", label: "Job", sort: true, value: (r) => r.jobName || "",
      render: (r) => <span className="font-medium" style={{ color: "var(--txt)" }}>{r.jobName}</span> },
    { key: "status", label: "Status", sort: true, value: (r) => r.success == null ? -1 : (r.success ? 1 : 0),
      render: (r) => <StatusBadge run={r} /> },
    { key: "startedAt", label: "Last run", sort: true, value: (r) => r.startedAt || "",
      render: (r) => (
        <div>
          <div style={{ color: "var(--txt)" }}>{relTime(r.startedAt)}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>{fmtDate(r.startedAt)}</div>
        </div>
      ) },
    { key: "duration", label: "Duration",
      render: (r) => <span className="tabular-nums text-[12px]" style={{ color: "var(--txt-2)" }}>{durationLabel(r.startedAt, r.finishedAt)}</span> },
    { key: "message", label: "Message", wrap: true,
      render: (r) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{r.message || "—"}</span> },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
            Scheduled jobs
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            Most recent run of every scheduled job. No history here yet means a job hasn't run since
            this tracking was added.
          </p>
        </div>
        <Button icon="refresh" size="sm" variant="ghost" onClick={fetchRuns} loading={loading}>Refresh</Button>
      </header>

      <AdminTable
        columns={columns}
        rows={runs}
        rowKey="id"
        loading={loading}
        emptyIcon="clock"
        emptyMessage="No job runs recorded yet — jobs only run in production, so a local dev instance won't show anything here."
        defaultSort={{ key: "jobName", dir: "asc" }}
      />
    </div>
  );
}
